/**
 * DocumentsSection — agent document library.
 *
 * Rendered in two places:
 *   - Property Admin Dashboard, "Documents" tab → no `agentId` prop, backend
 *     uses the property_admin session to scope requests.
 *   - Master Admin, Trading → Agents → per-agent Documents dialog → `agentId`
 *     prop is set, backend uses the master session and honours the query.
 *
 * Feature scope (per product spec):
 *   - Nested folders, delete-protect flag, files that can be uploaded,
 *     renamed, deleted, downloaded, viewed in a new tab, or shared via
 *     public tokenised link + emailed share.
 *   - Folders can't be deleted if they carry the delete-protect flag OR
 *     contain any files anywhere in their subtree (enforced server-side).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Folder, FolderPlus, ChevronRight, ChevronDown, Upload, Trash2,
  Pencil, Download, ExternalLink, Share2, Lock, Unlock, FileText,
  Loader2, ArrowLeft, Copy, Mail, Search, X,
} from "lucide-react";

const API = getApiUrl();

function _formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function _formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

export default function DocumentsSection({ agentId, propertyId, initialFolderName }) {
  // Build query-string helper so every request carries whichever scope
  // params apply: `agent_id=X` (master admin viewing an agent) and/or
  // `property_id=Y` (viewing one property's own document library).
  const scopeParams = useMemo(() => ({
    ...(agentId ? { agent_id: agentId } : {}),
    ...(propertyId ? { property_id: propertyId } : {}),
  }), [agentId, propertyId]);
  const withAgent = useCallback((extra = {}) => ({ ...scopeParams, ...extra }), [scopeParams]);

  const [folders, setFolders] = useState([]);       // flat list, tree via parent_folder_id
  const [documents, setDocuments] = useState([]);   // docs in current folder only
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [expanded, setExpanded] = useState(new Set()); // folder ids expanded in tree
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Cross-folder filename search. `searchQuery` is the raw input; when
  // non-empty we request `?search=` from the backend and render results as
  // a flat list with folder-path breadcrumbs so the admin can spot which
  // subtree each match lives in without hunting.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Drag-and-drop upload — the browser fires `dragenter`/`dragleave` on
  // every nested child element (buttons, table rows, …) which would flicker
  // the overlay off. We count entries in a ref (no re-render) and only
  // update `isDragging` when the count crosses 0.
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only react to real file drags, not text/element drags inside the app.
    if (!e.dataTransfer?.types?.includes("Files")) return;
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragCounter.current === 0) return;
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e) => {
    // Required for `drop` to fire. `dropEffect=copy` shows the standard
    // "+" cursor that tells the user this pane accepts the payload.
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) handleUpload(files);
  };

  // Dialog state
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameOpen, setRenameOpen] = useState(null);      // {kind:'folder'|'doc', id, name}
  const [renameValue, setRenameValue] = useState("");
  const [shareOpen, setShareOpen] = useState(null);        // full document object
  const [shareEmail, setShareEmail] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareSending, setShareSending] = useState(false);

  // ---------- Data fetch ----------
  const fetchFolders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/agent-documents/folders`, {
        params: scopeParams, withCredentials: true,
      });
      setFolders(data?.folders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load folders");
    }
  }, [scopeParams]);

  const fetchDocuments = useCallback(async (folderId) => {
    try {
      const params = { ...scopeParams };
      if (folderId) params.folder_id = folderId;
      const { data } = await axios.get(`${API}/agent-documents`, {
        params, withCredentials: true,
      });
      setDocuments(data?.documents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load documents");
    }
  }, [scopeParams]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchFolders(), fetchDocuments(currentFolderId)])
      .finally(() => setLoading(false));
  }, [fetchFolders, fetchDocuments, currentFolderId]);

  // When embedded somewhere that wants to land straight inside a specific
  // top-level folder (e.g. Edit Account's "ID Documents" preview) — jumps
  // in once the folder list has loaded, only on first load (`hasAutoNav`
  // guards against re-jumping if the admin navigates elsewhere after).
  const hasAutoNav = useRef(false);
  useEffect(() => {
    if (!initialFolderName || hasAutoNav.current || folders.length === 0) return;
    const target = folders.find(
      (f) => f.name === initialFolderName && !f.parent_folder_id,
    );
    if (target) {
      hasAutoNav.current = true;
      setCurrentFolderId(target.folder_id);
    }
  }, [folders, initialFolderName]);

  // Debounced cross-folder search. 200 ms feels instant but coalesces
  // fast typing so we don't slam the API on every keystroke.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/agent-documents`, {
          params: { ...scopeParams, search: q },
          withCredentials: true,
        });
        setSearchResults(data?.documents || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Search failed");
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [searchQuery, scopeParams]);

  // ---------- Derived tree ----------
  const foldersByParent = useMemo(() => {
    const map = new Map();
    folders.forEach((f) => {
      const key = f.parent_folder_id || "__root";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    });
    return map;
  }, [folders]);

  const breadcrumb = useMemo(() => {
    // Walk the parent chain so the header shows "Docs / A / B / C".
    if (!currentFolderId) return [];
    const byId = new Map(folders.map((f) => [f.folder_id, f]));
    const chain = [];
    let node = byId.get(currentFolderId);
    while (node) {
      chain.unshift(node);
      node = node.parent_folder_id ? byId.get(node.parent_folder_id) : null;
    }
    return chain;
  }, [currentFolderId, folders]);

  // Fast folder path lookup keyed by folder_id → "Contracts / 2026". Used
  // by search results so each hit shows where in the tree it lives without
  // re-walking the parent chain per row.
  const folderPathById = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.folder_id, f]));
    const cache = new Map();
    const walk = (id) => {
      if (!id) return "";
      if (cache.has(id)) return cache.get(id);
      const node = byId.get(id);
      if (!node) return "";
      const parentPath = node.parent_folder_id ? walk(node.parent_folder_id) : "";
      const path = parentPath ? `${parentPath} / ${node.name}` : node.name;
      cache.set(id, path);
      return path;
    };
    folders.forEach((f) => walk(f.folder_id));
    return cache;
  }, [folders]);

  // ---------- Folder ops ----------
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/agent-documents/folders`,
        { name, parent_folder_id: currentFolderId || null },
        { params: scopeParams, withCredentials: true },
      );
      setNewFolderName("");
      setNewFolderOpen(false);
      await fetchFolders();
      toast.success("Folder created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create folder");
    }
  };

  const handleRename = async () => {
    if (!renameOpen) return;
    const name = renameValue.trim();
    if (!name) return;
    try {
      if (renameOpen.kind === "folder") {
        await axios.patch(
          `${API}/agent-documents/folders/${renameOpen.id}`,
          { name },
          { params: scopeParams, withCredentials: true },
        );
        await fetchFolders();
      } else {
        await axios.patch(
          `${API}/agent-documents/${renameOpen.id}`,
          { filename: name },
          { params: scopeParams, withCredentials: true },
        );
        await fetchDocuments(currentFolderId);
      }
      setRenameOpen(null);
      toast.success("Renamed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const handleToggleProtect = async (folder) => {
    try {
      await axios.patch(
        `${API}/agent-documents/folders/${folder.folder_id}`,
        { delete_protected: !folder.delete_protected },
        { params: scopeParams, withCredentials: true },
      );
      await fetchFolders();
      toast.success(folder.delete_protected ? "Protection removed" : "Folder protected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!confirm(`Delete folder "${folder.name}" and any empty subfolders inside?`)) return;
    try {
      await axios.delete(`${API}/agent-documents/folders/${folder.folder_id}`, {
        params: scopeParams, withCredentials: true,
      });
      // If the current folder was just removed (or lives inside the removed
      // subtree), fall back to the parent so we never sit on a dangling id.
      if (currentFolderId === folder.folder_id) {
        setCurrentFolderId(folder.parent_folder_id || null);
      }
      await fetchFolders();
      toast.success("Folder deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ---------- Document ops ----------
  /**
   * Upload one or more files sequentially into the currently-open folder.
   * We upload one at a time so the "Uploaded 2 of 5" toast can update after
   * each file and a mid-batch failure doesn't skip the rest. Server-side
   * capacity (100 MB per file) is enforced with a proper 413; we surface
   * that back to the user without stopping the remaining queue.
   */
  const handleUpload = async (fileOrFiles) => {
    const files = Array.isArray(fileOrFiles)
      ? fileOrFiles
      : fileOrFiles instanceof FileList
        ? Array.from(fileOrFiles)
        : fileOrFiles ? [fileOrFiles] : [];
    if (!files.length) return;
    setUploading(true);
    const params = { ...scopeParams };
    if (currentFolderId) params.folder_id = currentFolderId;
    let succeeded = 0;
    const failures = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/agent-documents/upload`, form, {
          params, withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        succeeded += 1;
      } catch (e) {
        failures.push({ name: file.name, msg: e.response?.data?.detail || "Upload failed" });
      }
    }
    await fetchDocuments(currentFolderId);
    setUploading(false);
    if (succeeded) {
      toast.success(
        files.length === 1 ? "Uploaded" : `Uploaded ${succeeded} of ${files.length}`
      );
    }
    // Surface each failure individually so the admin can spot which file
    // was rejected (e.g. one PDF over 100 MB in a batch of 10).
    failures.forEach((f) => toast.error(`${f.name}: ${f.msg}`));
  };

  const handleDeleteDoc = async (doc) => {
    if (!confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/agent-documents/${doc.document_id}`, {
        params: scopeParams, withCredentials: true,
      });
      await fetchDocuments(currentFolderId);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const buildActionUrl = (doc, action) => {
    // Master-admin viewing an agent needs the agent_id query so the backend
    // authorises the download. Property-admin's own docs need no param.
    const qs = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : "";
    return `${API}/agent-documents/${doc.document_id}/${action}${qs}`;
  };

  const handleView = (doc) => window.open(buildActionUrl(doc, "view"), "_blank", "noopener");
  const handleDownload = (doc) => {
    // Same-tab download works because Content-Disposition: attachment forces
    // a save dialog rather than a navigation.
    window.location.href = buildActionUrl(doc, "download");
  };

  const openShareDialog = async (doc) => {
    // If sharing isn't enabled yet, flip it on so the token is minted before
    // we render the dialog.
    if (!doc.share_enabled || !doc.share_token) {
      try {
        const { data } = await axios.patch(
          `${API}/agent-documents/${doc.document_id}`,
          { share_enabled: true },
          { params: scopeParams, withCredentials: true },
        );
        setShareOpen(data);
        // Refresh the list so the "shared" badge shows.
        fetchDocuments(currentFolderId);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to enable sharing");
      }
    } else {
      setShareOpen(doc);
    }
  };

  const shareViewUrl = (token) =>
    `${window.location.origin}${API.replace(window.location.origin, "")}/agent-documents/shared/${token}/view`;

  const handleCopyShareLink = async () => {
    const url = shareViewUrl(shareOpen.share_token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const handleEmailShare = async () => {
    if (!shareEmail.trim()) return;
    setShareSending(true);
    try {
      const { data } = await axios.post(
        `${API}/agent-documents/${shareOpen.document_id}/share/email`,
        { to_email: shareEmail.trim(), message: shareMessage || null },
        { params: scopeParams, withCredentials: true },
      );
      toast.success(data.sent ? "Email sent" : "Link generated (email service unavailable)");
      setShareEmail("");
      setShareMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setShareSending(false);
    }
  };

  const handleRotateShare = async () => {
    if (!confirm("Any previously shared link will stop working. Continue?")) return;
    try {
      const { data } = await axios.post(
        `${API}/agent-documents/${shareOpen.document_id}/rotate-share`,
        {},
        { params: scopeParams, withCredentials: true },
      );
      setShareOpen({ ...shareOpen, share_token: data.share_token, share_enabled: true });
      toast.success("New link generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rotate failed");
    }
  };

  const handleUnshare = async () => {
    try {
      await axios.patch(
        `${API}/agent-documents/${shareOpen.document_id}`,
        { share_enabled: false },
        { params: scopeParams, withCredentials: true },
      );
      setShareOpen(null);
      fetchDocuments(currentFolderId);
      toast.success("Sharing disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const toggleExpand = (folderId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  // ---------- Tree row ----------
  // Recursive folder row — defined as a plain function that returns JSX so
  // it isn't re-declared as a new component on every render (React would
  // otherwise unmount + remount the whole subtree, killing expand state).
  const renderFolderRow = (folder, depth) => {
    const children = foldersByParent.get(folder.folder_id) || [];
    const isOpen = expanded.has(folder.folder_id);
    const isActive = currentFolderId === folder.folder_id;
    return (
      <div key={folder.folder_id}>
        <div
          className={`flex items-center gap-1 py-1.5 pr-2 rounded cursor-pointer group ${
            isActive ? "bg-primary/10 text-primary" : "hover:bg-muted"
          }`}
          style={{ paddingLeft: `${depth * 14 + 4}px` }}
          data-testid={`folder-row-${folder.folder_id}`}
        >
          <button
            type="button"
            onClick={() => toggleExpand(folder.folder_id)}
            className="p-0.5"
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {children.length > 0 ? (
              isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
            ) : <span className="inline-block w-3" />}
          </button>
          <button
            type="button"
            onClick={() => setCurrentFolderId(folder.folder_id)}
            className="flex-1 flex items-center gap-1.5 min-w-0 text-left text-sm"
          >
            <Folder className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{folder.name}</span>
            {folder.delete_protected && (
              <Lock className="h-3 w-3 text-amber-600 shrink-0" title="Delete-protected" />
            )}
          </button>
        </div>
        {isOpen && children.map((c) => renderFolderRow(c, depth + 1))}
      </div>
    );
  };

  const rootFolders = foldersByParent.get("__root") || [];

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[260px,1fr] gap-4 relative"
      data-testid="documents-section"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag-over overlay — sits above both panes so a drop anywhere lands
          in the currently-open folder. Only renders while a file drag is
          active so it never blocks normal clicks. */}
      {isDragging && (
        <div
          className="absolute inset-0 z-30 bg-primary/10 border-2 border-dashed border-primary rounded-md flex items-center justify-center pointer-events-none"
          data-testid="documents-dropzone"
        >
          <div className="bg-white/95 border border-primary/40 rounded-lg px-6 py-4 shadow-lg text-center">
            <Upload className="h-8 w-8 text-primary mx-auto mb-1" />
            <p className="text-sm font-medium text-primary">
              Drop to upload
              {breadcrumb.length > 0 && (
                <> into <strong>{breadcrumb[breadcrumb.length - 1].name}</strong></>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Up to 100 MB per file</p>
          </div>
        </div>
      )}
      {/* Left: folder tree */}
      <aside className="bg-muted/20 border border-border p-3 rounded-md space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Folders
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setNewFolderOpen(true)}
            data-testid="new-folder-btn"
          >
            <FolderPlus className="h-3.5 w-3.5 mr-1" /> New
          </Button>
        </div>
        <button
          type="button"
          onClick={() => setCurrentFolderId(null)}
          className={`w-full text-left text-sm px-2 py-1.5 rounded ${
            currentFolderId === null ? "bg-primary/10 text-primary" : "hover:bg-muted"
          }`}
          data-testid="root-folder"
        >
          <Folder className="h-3.5 w-3.5 inline mr-1.5" />
          All documents
        </button>
        <div className="max-h-[480px] overflow-y-auto">
          {rootFolders.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-3">
              No folders yet. Click &quot;New&quot; to create one.
            </p>
          )}
          {rootFolders.map((f) => renderFolderRow(f, 0))}
        </div>
      </aside>

      {/* Right: file list */}
      <section className="bg-white border border-border rounded-md p-4 space-y-4 min-h-[480px]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <button
                onClick={() => setCurrentFolderId(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                Documents
              </button>
              {breadcrumb.map((f) => (
                <span key={f.folder_id} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  <button
                    onClick={() => setCurrentFolderId(f.folder_id)}
                    className="text-foreground font-medium"
                  >
                    {f.name}
                  </button>
                </span>
              ))}
            </div>
            {/* Folder-specific actions (rename / protect / delete). Root
                has no folder object, so we hide these when at root. */}
            {breadcrumb.length > 0 && (() => {
              const current = breadcrumb[breadcrumb.length - 1];
              return (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                    onClick={() => { setRenameOpen({ kind: "folder", id: current.folder_id, name: current.name }); setRenameValue(current.name); }}
                    data-testid="folder-rename-btn"
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Rename
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                    onClick={() => handleToggleProtect(current)}
                    data-testid="folder-protect-btn"
                  >
                    {current.delete_protected ? (
                      <><Unlock className="h-3 w-3 mr-1" /> Remove protection</>
                    ) : (
                      <><Lock className="h-3 w-3 mr-1" /> Delete-protect</>
                    )}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => handleDeleteFolder(current)}
                    data-testid="folder-delete-btn"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            {currentFolderId && (
              <Button
                variant="outline" size="sm"
                onClick={() => {
                  const parent = breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].folder_id : null;
                  setCurrentFolderId(parent);
                }}
              >
                <ArrowLeft className="h-3 w-3 mr-1" /> Up
              </Button>
            )}
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded cursor-pointer hover:bg-primary/90">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  handleUpload(files);
                }}
                data-testid="upload-input"
              />
            </label>
          </div>
        </div>

        {/* Cross-folder filename search. Empty query → normal current-folder
            view. Non-empty → flat results across the whole tree with a
            folder-path column so hits are easy to locate. */}
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search files across all folders…"
            className="pl-8 pr-8"
            data-testid="documents-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
              aria-label="Clear search"
              data-testid="documents-search-clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* File table — swaps between current-folder docs and search results.
            During search we surface a "Path" column so the admin can spot
            which subtree a match lives in and jump straight there. */}
        {(() => {
          const isSearchMode = searchQuery.trim().length > 0;
          const rows = isSearchMode ? searchResults : documents;
          const busy = isSearchMode ? searching : loading;
          if (busy) {
            return (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            );
          }
          if (rows.length === 0) {
            return (
              <div className="text-center py-16 border border-dashed border-border rounded">
                <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                {isSearchMode ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      No files match &quot;{searchQuery}&quot;.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try a shorter or different keyword.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">No documents here yet.</p>
                    <p className="text-xs text-muted-foreground mt-1">Upload a file to get started (up to 100 MB).</p>
                  </>
                )}
              </div>
            );
          }
          return (
            <div className="border border-border rounded overflow-x-auto">
              {isSearchMode && (
                <div className="px-3 py-1.5 bg-muted/30 text-xs text-muted-foreground border-b border-border">
                  {rows.length} result{rows.length === 1 ? "" : "s"} across all folders
                </div>
              )}
              <table className="w-full text-sm min-w-[480px]">
                <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left py-2 px-3">Name</th>
                    {isSearchMode && (
                      <th className="text-left py-2 px-3 hidden md:table-cell">Folder</th>
                    )}
                    <th className="text-left py-2 px-3 hidden sm:table-cell">Size</th>
                    <th className="text-left py-2 px-3 hidden md:table-cell">Uploaded</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((doc) => (
                    <tr key={doc.document_id} className="border-t border-border hover:bg-muted/20" data-testid={`doc-row-${doc.document_id}`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{doc.filename}</span>
                          {doc.source === "verify_photo" && doc.verified === false && (
                            <span
                              className="text-[10px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded whitespace-nowrap"
                              title="A failed or low-score ID verification attempt — review before relying on it"
                              data-testid={`doc-needs-review-${doc.document_id}`}
                            >
                              Needs review
                            </span>
                          )}
                          {doc.share_enabled && (
                            <span className="text-[10px] uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                              Shared
                            </span>
                          )}
                        </div>
                      </td>
                      {isSearchMode && (
                        <td className="py-2 px-3 hidden md:table-cell">
                          {/* Click the folder chip to jump straight to the
                              match's parent folder and clear the search. */}
                          <button
                            type="button"
                            onClick={() => {
                              setSearchQuery("");
                              setCurrentFolderId(doc.folder_id || null);
                            }}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            data-testid={`doc-goto-folder-${doc.document_id}`}
                          >
                            <Folder className="h-3 w-3" />
                            {doc.folder_id ? (folderPathById.get(doc.folder_id) || "…") : "All documents"}
                          </button>
                        </td>
                      )}
                      <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{_formatBytes(doc.size)}</td>
                      <td className="py-2 px-3 text-muted-foreground hidden md:table-cell">{_formatDate(doc.uploaded_at)}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            className="p-1.5 rounded hover:bg-muted"
                            title="View"
                            onClick={() => handleView(doc)}
                            data-testid={`doc-view-${doc.document_id}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="p-1.5 rounded hover:bg-muted"
                            title="Download"
                            onClick={() => handleDownload(doc)}
                            data-testid={`doc-download-${doc.document_id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="p-1.5 rounded hover:bg-muted"
                            title="Share"
                            onClick={() => openShareDialog(doc)}
                            data-testid={`doc-share-${doc.document_id}`}
                          >
                            <Share2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="p-1.5 rounded hover:bg-muted"
                            title="Rename"
                            onClick={() => { setRenameOpen({ kind: "doc", id: doc.document_id, name: doc.filename }); setRenameValue(doc.filename); }}
                            data-testid={`doc-rename-${doc.document_id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="p-1.5 rounded hover:bg-muted text-destructive"
                            title="Delete"
                            onClick={() => handleDeleteDoc(doc)}
                            data-testid={`doc-delete-${doc.document_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </section>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="new-folder-name">Folder name</Label>
            <Input
              id="new-folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              placeholder="e.g. Contracts"
              data-testid="new-folder-input"
              autoFocus
            />
            {currentFolderId && (
              <p className="text-xs text-muted-foreground">
                Will be created inside{" "}
                <strong>{breadcrumb[breadcrumb.length - 1]?.name}</strong>.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()} data-testid="new-folder-save">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameOpen} onOpenChange={(v) => !v && setRenameOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {renameOpen?.kind === "folder" ? "folder" : "document"}</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
            data-testid="rename-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(null)}>Cancel</Button>
            <Button onClick={handleRename} disabled={!renameValue.trim()} data-testid="rename-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={!!shareOpen} onOpenChange={(v) => !v && setShareOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share &quot;{shareOpen?.filename}&quot;</DialogTitle>
          </DialogHeader>
          {shareOpen && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sharable link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={shareViewUrl(shareOpen.share_token)}
                    className="font-mono text-xs"
                    data-testid="share-link"
                  />
                  <Button variant="outline" onClick={handleCopyShareLink} data-testid="share-copy">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Anyone with this link can view or download the file.
                </p>
              </div>

              <div className="border-t pt-4 space-y-2">
                <Label>Or email it to someone</Label>
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  data-testid="share-email"
                />
                <Textarea
                  placeholder="Optional note…"
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  rows={2}
                  data-testid="share-message"
                />
                <Button
                  onClick={handleEmailShare}
                  disabled={!shareEmail.trim() || shareSending}
                  data-testid="share-send"
                  className="w-full"
                >
                  {shareSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Send email
                </Button>
              </div>

              <div className="border-t pt-4 flex items-center justify-between gap-2">
                <Button variant="outline" size="sm" onClick={handleRotateShare} data-testid="share-rotate">
                  Rotate link
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={handleUnshare} data-testid="share-disable">
                  Disable sharing
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
